import { useState } from 'react';
import { Button, Chip } from '@nyce/admin-kit/ui';
import { db, type DbBackup, type DbServerRef } from '../../api/db';
import {
  EmptyState,
  ErrorBox,
  Loading,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useSort,
  type SortKeys,
} from '../../components/common';
import { useParam } from '../statistics/shared';
import { formatBytes, formatDateTime, formatRelative } from '../../lib/format';
import { DatabasePicker, dbErrorText, startDownload, useServerDatabases, type DbScopes } from './shared';

/** The file part of a GCS object name. */
const baseName = (object: string) => object.split('/').pop() || object;

const BACKUP_SORT: SortKeys<DbBackup> = {
  object: { label: 'Object', value: (b) => baseName(b.object) },
  size: { label: 'Size', value: (b) => b.sizeBytes, desc: true },
  created: { label: 'Created', value: (b) => b.createdAt, desc: true },
  kind: { label: 'Kind', value: (b) => b.kind },
  job: { label: 'Job', value: (b) => b.jobId, desc: true },
};

export function BackupsTab({
  server,
  database,
  onDatabase,
  scopes,
  onOpenJob,
}: {
  server: DbServerRef;
  database: string;
  onDatabase: (db: string) => void;
  scopes: DbScopes;
  onOpenJob: (id: number) => void;
}) {
  const dbs = useServerDatabases(server.id);
  const list = useAsync(() => db.backups(server.id, database || undefined), [server.id, database]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sort = useSort(list.data, BACKUP_SORT, useParam('sort', ''));
  const rows = sort.rows;

  const download = async (b: DbBackup) => {
    if (b.jobId == null) return;
    setBusy(b.object);
    setError(null);
    try {
      const r = await db.jobs.download(b.jobId);
      startDownload(r.url, baseName(b.object));
    } catch (e) {
      setError(dbErrorText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="dbx-toolbar">
        <DatabasePicker databases={dbs.names} value={database} onChange={onDatabase} all="All databases" />
        <Button size="sm" variant="ghost" onClick={() => void list.reload()}>
          Refresh
        </Button>
        <SortSelect sort={sort} />
      </div>
      <ErrorBox error={error} />
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !rows.length && (
        <EmptyState
          card
          title="No backups in the bucket"
          hint={scopes.backup ? 'Start one with Backup now on the Overview tab.' : undefined}
        />
      )}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="db-backups">
              <thead>
                <tr>
                  <SortTh sort={sort} by="object" />
                  <SortTh sort={sort} by="size" className="nyk-ui-num" />
                  <SortTh sort={sort} by="created" />
                  <SortTh sort={sort} by="kind" />
                  <SortTh sort={sort} by="job" />
                  {scopes.backup && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.object} data-row={b.object}>
                    <td className="nyk-card-title" title={b.object}>
                      <span className="nyk-url">{baseName(b.object)}</span>
                    </td>
                    <td className="nyk-ui-num" data-label="Size">
                      {formatBytes(b.sizeBytes)}
                    </td>
                    <td data-label="Created" title={formatDateTime(b.createdAt)}>
                      {formatRelative(b.createdAt)}
                    </td>
                    <td data-label="Kind">
                      <Chip size="sm" className={`dbx-kind dbx-kind-${b.kind}`}>
                        {b.kind}
                      </Chip>
                    </td>
                    <td data-label="Job">
                      {b.jobId != null ? (
                        <button type="button" className="nyk-link" onClick={() => onOpenJob(b.jobId!)}>
                          #{b.jobId}
                        </button>
                      ) : (
                        <span className="dbx-muted">—</span>
                      )}
                    </td>
                    {scopes.backup && (
                      <td className="nyk-row-actions">
                        {b.jobId != null && (
                          <Button
                            size="sm"
                            variant="ghost"
                            loading={busy === b.object}
                            onClick={() => void download(b)}
                          >
                            Download
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
    </>
  );
}
